import { Component, effect, inject } from '@angular/core';
import { CategoryStore } from '../../../stores/category/category.state';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Indicator } from '../../../models/indicator';
import { IndicatorStore } from '../../../stores/indicator/indicator.state';
import { ColorsetStore } from '../../../stores/colorset/colorset.state';
import { CheckboxModule } from 'primeng/checkbox';
import { DividerModule } from 'primeng/divider';
import { DropdownModule } from 'primeng/dropdown';
import { FormFieldWrapperComponent } from '../../molecules/form-field-wrapper/form-field-wrapper.component';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { MultiSelectModule } from 'primeng/multiselect';
import { NgClass, NgForOf, NgIf } from '@angular/common';
import { PaginatorModule } from 'primeng/paginator';
import { EditorModule } from 'primeng/editor';
import { Unit, unitsAsArray } from '../../../models/unit';
import { TreeSelectModule } from 'primeng/treeselect';
import { MenuItem } from 'primeng/api';
import { TabMenuModule } from 'primeng/tabmenu';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputSwitchModule } from 'primeng/inputswitch';
import { DragDropModule } from 'primeng/dragdrop';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { legendModes } from '../../../models/legend';
import { HeaderComponent } from '../../atoms/header/header.component';
import { MaskComponent } from '../mask/mask.component';
import { SidebarModule } from 'primeng/sidebar';
import { getState, patchState } from '@ngrx/signals';
import { keyValidator } from '../../../validators/key.validator';
import { TagDropdownComponent } from '../../atoms/tag-dropdown/tag-dropdown.component';
import { AppUtilityService } from '../../../utility/app.utility';
import { RoutingService } from '../../../services/routing.service';
import { BaseFormComponent } from '../../molecules/base-form/base-form.component';
import { PermissionService } from '../../../services/permission.service';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { FormFooterComponent } from '../../molecules/form-footer/form-footer.component';
import { ColorsetDropdownComponent } from '../../atoms/colorset-dropdown/colorset-dropdown.component';
import { LegendFormComponent } from '../../molecules/legend-form/legend-form.component';
import { AlertComponent } from '../../atoms/alert/alert.component';

@Component({
  selector: 'app-indicator-form',
  standalone: true,
  imports: [
    CheckboxModule,
    DividerModule,
    DropdownModule,
    FormFieldWrapperComponent,
    InputTextModule,
    InputTextareaModule,
    MultiSelectModule,
    NgIf,
    PaginatorModule,
    ReactiveFormsModule,
    EditorModule,
    TreeSelectModule,
    TabMenuModule,
    ButtonModule,
    NgForOf,
    InputSwitchModule,
    DragDropModule,
    CdkDropList,
    CdkDrag,
    HeaderComponent,
    MaskComponent,
    SidebarModule,
    TagDropdownComponent,
    FormFooterComponent,
    ColorsetDropdownComponent,
    LegendFormComponent,
    AlertComponent,
    NgClass,
  ],
  templateUrl: './indicator-form.component.html',
  styleUrl: './indicator-form.component.scss',
})
export class IndicatorFormComponent extends BaseFormComponent<Indicator> {
  readonly store = inject(IndicatorStore);
  readonly categoryStore = inject(CategoryStore);
  readonly colorsetStore = inject(ColorsetStore);

  override form: FormGroup = this.fb.group({});
  override itemToEdit?: Indicator;

  units: Unit[] = unitsAsArray();
  childrenToRemove: Indicator[] = [];
  isChild = false;
  childFormOverlay = false;
  childrenUpdated = false;
  parent?: Indicator;

  // tab-menu
  tabItems: MenuItem[] = [];
  activeTabItem: MenuItem = this.tabItems[0];
  selectedTab: string | null | undefined = 'general';
  indicatorKeys: string[] = [];

  constructor(
    router: Router,
    route: ActivatedRoute,
    appUtilityService: AppUtilityService,
    routingService: RoutingService,
    permissionService: PermissionService,
    confirmationDialogService: ConfirmationDialogService,
    private fb: FormBuilder
  ) {
    super(
      router,
      route,
      appUtilityService,
      routingService,
      permissionService,
      confirmationDialogService
    );

    this.initializeMenuItems();

    effect(() => {
      const indicatorState = getState(this.store);
      const colorsetState = getState(this.colorsetStore);

      if (colorsetState.selectedId) {
        this.form.patchValue({ defaultColorSet: colorsetState.selectedId });
      }

      if (indicatorState.closeChild && !this.isChild) {
        setTimeout(() => {
          this.onChildOverlayClose();
        });
      }

      if (colorsetState.selectedId && this.formInitialized) {
        setTimeout(() => {
          this.form.patchValue({ defaultColorSet: colorsetState.selectedId });
        });
      }

      this.indicatorKeys = this.store.allKeys();

      queueMicrotask(() => {
        this.form
          .get('key')
          ?.setValidators([
            keyValidator(this.indicatorKeys, this.itemToEdit?.key),
          ]);
      });
    });
  }

  /**
   * Initializes the form by pre-filling its fields based on the selected item or child item.
   * Determines and sets the active tab based on route parameters. Configures form controls with
   * default values or values derived from the selected item/child including validation rules.
   * Also handles special cases such as setting child items if the current item is a set.
   *
   * @return {void} This method does not return a value.
   */
  initializeForm(): void {
    this.selectedTab =
      this.routingService.getParameterFromRoute('tab') || 'general';
    if (this.selectedTab !== 'general') this.activeTabItem = this.tabItems[1];

    const selectedChild = this.store.selectedChild
      ? this.store.selectedChild()
      : undefined;

    if (selectedChild) {
      this.itemToEdit = this.store.selectedChild();
      this.isChild = true;
      this.parent = this.store.getById(selectedChild.parent);
    } else {
      this.itemToEdit = this.store.selectedItem();
      this.isChild = false;
    }

    const parentFromQueryParams =
      this.routingService.getQueryParamFromRoute('categoryId');

    this.form = this.fb.group({
      _id: [this.itemToEdit?._id || ''],
      name: [this.itemToEdit?.name || '', Validators.required],
      key: [
        this.itemToEdit?.key || '',
        keyValidator(this.indicatorKeys, this.itemToEdit?.key),
      ],
      category: [
        parentFromQueryParams
          ? this.categoryStore.getByIdAsTreeItem(parentFromQueryParams)
          : this.itemToEdit && this.itemToEdit.category
            ? this.categoryStore.getByIdAsTreeItem(this.itemToEdit.category)
            : '',
        this.isChild ? undefined : Validators.required,
      ],
      unit: [this.itemToEdit?.unit || '1'],
      categorical: [this.itemToEdit?.categorical || false],
      tags: [this.itemToEdit?.tags || []],
      products: [this.itemToEdit?.products || []],
      decimals: [this.itemToEdit?.decimals || undefined],
      defaultColorSet: [this.itemToEdit?.defaultColorSet || undefined],
      defaultInvert: [this.itemToEdit?.defaultInvert || false],
      indication: [this.itemToEdit?.indication || ''],
      hint: [this.itemToEdit?.hint || ''],
      source: [this.itemToEdit?.source || ''],
      statement: [this.itemToEdit?.statement || ''],
      calculation: [this.itemToEdit?.calculation || ''],
      sorting: [this.itemToEdit?.sorting || 0],
      dataCycle: [this.itemToEdit?.dataCycle || ''],
      levelOfAvailability: [this.itemToEdit?.levelOfAvailability || ''],
      dataSupplier: [this.itemToEdit?.dataSupplier || ''],
      isSet: [this.itemToEdit?.isSet || false],
      locked: [this.itemToEdit?.locked || false],
      pool: [this.itemToEdit?.pool || false],
      legendMode: [
        this.itemToEdit?.legendMode ||
        (this.itemToEdit?.legend ? 'fixed' : 'default'),
      ],
      legend: this.fb.array(this.itemToEdit?.legend || []),
      children: this.fb.array([]),
    });

    // todo outsource to methods
    if (this.itemToEdit && this.itemToEdit.isSet) {
      this.setChildrenFromParent();
    }

    this.formInitialized = true;
  }

  /**
   * Handles the change event for the legend mode selection.
   * Clears the `legendFormArray` if the selected value is not 'fixed'.
   *
   * @param {any} e - The event object containing the selected value.
   * @return {void} Does not return any value.
   */
  legendModeChange(e: any) {
    if (!e.value || e.value !== 'fixed') {
      this.legendFormArray.clear();
    }
  }

  /**
   * Sets child elements from the parent by retrieving children from the store
   * and adding them as child elements.
   *
   * @return {void} This method does not return a value.
   */
  setChildrenFromParent(): void {
    this.store.childrenFromSelected().forEach((indicator: Indicator, idx) => {
      this.addChild(indicator, idx);
    });
  }

  /**
   * Retrieves the 'legend' form array from the form group.
   *
   * @return {FormArray} The 'legend' form array cast from the form control.
   */
  public get legendFormArray(): FormArray {
    return this.form.get('legend') as FormArray;
  }

  /**
   * Gets the 'children' form control casted as a FormArray.
   *
   * @return {FormArray} The FormArray instance representing the 'children' form control.
   */
  get childFormArray(): FormArray {
    return this.form.get('children') as FormArray;
  }

  /**
   * Adds a new child indicator to the childFormArray. If a child object is provided,
   * its properties are initialized into the form group. Otherwise, default values are used.
   *
   * @param {Indicator} [child] - An optional indicator object whose properties
   * @param {number} [idx] - The index of the child-array-item
   * will initialize the form group.
   * @return {void} This method does not return a value.
   */
  addChild(child?: Indicator, idx?: number): void {
    const childGroup = this.fb.group({
      key: [child ? child.key : '', keyValidator(this.indicatorKeys)],
      name: [child ? child.name : [], Validators.required],
      _id: [child ? child._id : ''],
      unit: [child?.unit || '1'],
      tags: [child?.tags || []],
      decimals: [child?.decimals || undefined],
      defaultColorSet: [child?.defaultColorSet || undefined],
      defaultInvert: [child?.defaultInvert || false],
      indication: [child?.indication || ''],
      hint: [child?.hint || ''],
      source: [child?.source || ''],
      statement: [child?.statement || ''],
      calculation: [child?.calculation || ''],
      //sorting: [child?.sorting || this.childFormArray.length],
      sorting: [idx ?? this.childFormArray.length],
      dataCycle: [child?.dataCycle || ''],
      levelOfAvailability: [child?.levelOfAvailability || ''],
      dataSupplier: [child?.dataSupplier || ''],
      locked: [child?.locked || false],
      legendMode: [child?.legendMode || 'default'],
      legend: this.fb.array(child?.legend || []),
      status: child ? 'check' : 'new',
    });
    this.childFormArray.push(childGroup);
  }

  /**
   * Removes a child element from the childFormArray at the specified index.
   * If the child has an `_id`, it marks the child as deleted and adds it to the
   * childrenToRemove array.
   *
   * @param {number} index - The index of the child element to be removed.
   * @return {void}
   */
  removeChild(index: number) {
    const childToRemove = this.childFormArray.value[index];

    if (childToRemove._id) {
      childToRemove.deleted = true;
      this.childrenToRemove.push(this.childFormArray.value[index]);
    }

    this.childFormArray.removeAt(index);
  }

  /**
   * Handles the drop event of a drag-and-drop operation. Updates the order
   * of controls in a FormArray and optionally reflects these changes back
   * to the form array values.
   *
   * @param {CdkDragDrop<string[]>} event - The drag-and-drop event containing
   * the previous and current index of the item being moved.
   *
   * @return {void} This method does not return a value.
   */
  drop(event: CdkDragDrop<string[]>): void {
    moveItemInArray(
      this.childFormArray.controls,
      event.previousIndex,
      event.currentIndex
    );

    // If you need to reflect these changes back to the form array values:
    moveItemInArray(
      (this.form.get('children') as FormArray).value,
      event.previousIndex,
      event.currentIndex
    );

    // Update sorting values for all children based on their new positions
    this.childFormArray.controls.forEach((control, index) => {
      control.patchValue({ sorting: index });
    });

    //this.childFormArray.updateValueAndValidity();
  }

  /**
   * Removes the specified indicator from the system.
   * This action prompts the user with a confirmation dialog before proceeding with the deletion.
   *
   * @param {Indicator} item - The indicator object to be removed.
   * @return {void} This method does not return any value.
   */
  removeIndicator(item: Indicator): void {
    this.confirmationDialogService.confirmIndicatorDeletion(
      undefined,
      item,
      () => this.remove(item)
    );
  }

  /**
   * Handles the submission of the form. Validates the form and performs appropriate updates and navigation based on the context.
   *
   * - If the form is valid and the entity is a child, it updates the child entity and closes the form.
   * - If the form is valid and the entity is not a child:
   *   - Updates the primary entity in the store, including the category property.
   *   - Ensures any removed children are marked as deleted in the store.
   *   - Updates the store with new child entities.
   *   - Displays a confirmation message and navigates back to the previous route.
   * - If the form is invalid, marks all fields as touched to show validation errors.
   *
   * @return {void} This method does not return a value.
   */
  onSubmit(): void {
    if (this.form.valid) {
      if (this.isChild) {
        this.store.updateChildAndClose(this.enhanceFormValues());
        this.confirmationDialogService.showMessage(
          'Aktualisierung übertragen',
          'Um die Ausprägung zu speichern, speichern Sie auch den Indikator!',
          5000
        );
      } else {
        // hook category
        this.store.updateMultiple({
          items: [this.enhanceFormValues()],
        });

        // set deleted for removed children
        if (this.childrenToRemove.length) {
          this.store.updateMultiple({
            items: this.childrenToRemove,
            message: { avoid: true },
          });
        }

        this.updateStoreChildren();

        // add children to store
        //this.confirmationDialogService.updateMessage(this.form.value.name);
        this.routingService.navigateBack(this.cancelLevel);
      }
    } else {
      // Mark all form fields as touched to display validation errors
      this.form.markAllAsTouched();
      this.appUtilityService.markAllAsDirty(this.form);
    }
  }

  /**
   * Enhances the current form values by returning a new object that merges the existing form values
   * with a transformation for the "category" field, replacing it with the "_id" property of the category.
   *
   * @return {Object} A new object containing the enhanced form values with the transformed "category" field.
   */
  enhanceFormValues() {
    return {
      ...this.form.value,
      ...{ category: this.form.value.category._id },
    };
  }

  /**
   * Initializes the menu items based on the currently selected child.
   *
   * @return {void} This method does not return a value. It sets the `tabItems` property to an array of menu items.
   */
  initializeMenuItems(): void {
    this.tabItems = !this.store.selectedChild()
      ? [
        {
          label: 'Grundeinstellungen',
          icon: 'fas fa-ruler-vertical',
          id: 'general',
          routerLink: ['../general'],
        },
        {
          label: 'Ausprägungen',
          icon: 'fas fa-ruler-combined',
          id: 'indicator-set',
          routerLink: ['../indicator-set'],
        },
        {
          label: 'Metainformationen',
          icon: 'fas fa-circle-info',
          id: 'meta',
          routerLink: ['../meta'],
        },
        {
          label: 'Farben und Klassen',
          icon: 'fas fa-palette',
          id: 'legend',
          routerLink: ['../legend'],
        },
      ]
      : [
        {
          label: 'Grundeinstellungen',
          icon: 'fas fa-ruler-vertical',
          id: 'general',
          routerLink: ['../general'],
        },
        {
          label: 'Metainformationen',
          icon: 'fas fa-circle-info',
          id: 'meta',
          routerLink: ['../meta'],
        },
        {
          label: 'Farben und Klassen',
          icon: 'fas fa-palette',
          id: 'legend',
          routerLink: ['../legend'],
        },
      ];
  }

  /**
   * Selects a child item based on the provided values and updates the application state
   * by navigating to the corresponding path and updating the child form overlay.
   *
   * @param {Object} values - The values used to select the child. Contains contextual data including the child ID.
   * @return {void} This method does not return a value.
   */
  selectChild(values: any): void {
    this.store.selectChild({
      ...values,
      ...{
        category: this.form.value.category._id,
      },
    });
    this.childFormOverlay = true;
    this.router.navigate(
      [
        {
          outlets: {
            auspraegung: [values._id || 'neu', 'general'],
          },
        },
      ],
      { relativeTo: this.route }
    );
  }

  /**
   * Handles the closure of a child overlay by resetting the overlay state, navigating back
   * to the parent route, and updating the child form array with the latest values from the store.
   *
   * @return {void} No return value.
   */
  onChildOverlayClose(): void {
    this.childFormOverlay = false;
    this.router.navigate(['./'], {
      relativeTo: this.route,
    });

    // update child
    const newChildValues = this.store.selectedChild();
    if (newChildValues && this.childFormArray.value[newChildValues.sorting]) {
      newChildValues.status = newChildValues._id ? 'edited' : 'new';
      if (newChildValues.legend) {
        const legendFormArray = this.childFormArray
          .at(newChildValues.sorting)
          .get('legend') as FormArray;
        legendFormArray.clear();
        newChildValues.legend.forEach((value: any) =>
          legendFormArray.push(this.fb.control(value))
        );
      }
      this.childFormArray.at(newChildValues.sorting).patchValue(newChildValues);
    }

    queueMicrotask(() => {
      this.store.deselectChild();
    });
  }

  /**
   * Updates the store with the non-deleted children data derived from the childFormArray.
   * Iterates through the childFormArray values and filters out deleted children.
   * Merges the non-deleted children data with the existing store items while associating
   * the respective parent and category information.
   * Applies the updated data to the store.
   *
   * @return {void} Does not return a value.
   */
  updateStoreChildren() {
    if (this.childFormArray.valid) {
      const childValues = this.childFormArray.value.filter(
        (child: any) => !child.deleted
      );

      if (childValues.length) {
        const updatedItems = { ...this.store.items() };

        childValues.forEach((child: any) => {
          if (child._id) {
            updatedItems[child._id] = {
              ...child,
              parent: this.form.value._id,
              category: this.form.value.category._id,
            };
          }
        });

        patchState(this.store, {
          items: updatedItems,
        });
      }
    }
  }

  /**
   * Removes HTML tags from a string.
   *
   * @param {string} value - The string from which HTML tags will be removed.
   * @return {string} The string with HTML tags removed.
   */
  getTextFromHtml(value?: string): string {
    return value?.replace(/<[^>]*>/g, '') || '';
  }

  /**
   * Handles the event when a tab is changed and updates the active tab item and selected tab identifier.
   *
   * @param {MenuItem} $event - The event object representing the selected tab item.
   * @return {void} This method does not return any value.
   */
  onTabChange($event: MenuItem): void {
    this.activeTabItem = $event;
    this.selectedTab = this.activeTabItem.id || 'general';
  }

  protected readonly legendModes = legendModes;

  /**
   * Handles the cancel action for the current context.
   * If the object is a child, it closes the child overlay using the store.
   *
   * @return {void} This method does not return a value.
   */
  onCancel(): void {
    if (this.isChild) {
      this.store.closeChildOverlay();
    }
  }
}
